import { ApolloError, FetchResult, useMutation } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Mutations } from '@shared/api';
import { useLocalWorkflowEntityChanges } from '@shared/data/cards/hooks/LocalWorkflowEntityChanges';
import { API } from '@shared/types/api';

interface UpdateWorkflowEntity {
  updateWorkflowEntity: (
    id: string,
    fields: any,
    viewTemplateId?: string | null,
  ) => Promise<FetchResult<API.UpdateWorkflowEntityMutation>>;
  loading: boolean;
  error: ApolloError | undefined;
}

export function useUpdateWorkflowEntity(): UpdateWorkflowEntity {
  const [update, { loading, error }] = useMutation<
    API.UpdateWorkflowEntityMutation,
    API.UpdateWorkflowEntityMutationVariables
  >(Mutations.entity.updateWorkflowEntity);
  const { readCards, publish } = useLocalWorkflowEntityChanges();

  const updateWorkflowEntity = useCallback(
    (id: string, fields: any, viewTemplateId?: string | null) => {
      // A caller that writes the cache before calling this, as drag and drop does when it moves a
      // card between groups, has its moved version read here as the one before the change. The
      // change then nets to zero, as does its event, rather than moving the card a second time.
      const previousEntities = readCards([id]);
      return update({
        variables: {
          input: {
            id,
            fields,
            viewTemplateId,
          },
        },
      }).then(result => {
        const entity = result.data?.updateWorkflowEntity?.entity;
        if (!isNil(entity)) {
          publish({ type: 'WORKFLOW_ENTITY_UPDATED', entities: [entity], previousEntities });
        }
        return result;
      });
    },
    [update, readCards, publish],
  );

  return useMemo(() => {
    return {
      updateWorkflowEntity,
      loading,
      error,
    };
  }, [updateWorkflowEntity, loading, error]);
}
